import React, { useContext, useMemo } from 'react'
import { Button } from 'react-bootstrap';
import { Accordion, Card } from 'react-bootstrap';
import styles from './AssetSection.module.css';
import AssetCard from '../../components/AssetCard/AssetCard';
import { RootContext } from '../../hoc/RootContext/RootContext';
import { ASSET_SECTION_LABELS } from '../../constants';
import { useIntl } from 'react-intl';
import { slugify } from '../../utils/utils';
const classNames = require('classnames');

export default function AssetSection({ title, assets, showAssetModal, openSection, readOnly, spacePages, fieldSettings }) {
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });
  const { assetSchema, sectionSchema } = useContext(RootContext);
  const sectionKey = title.toLowerCase().replace(/ /g, '_')
  const byUnit = (a, b) => a.unit > b.unit ? 1 : -1

  const filteredAndSortedAssets = useMemo(() => {
    const hasSchema = asset => assetSchema?.[sectionKey].find(schema => schema.type === asset.type)
    return assets ? assets.filter(hasSchema).sort(byUnit) : null
  }, [assets, assetSchema, sectionKey])

  if (!assetSchema) return null;

  const handleCardClick = (asset, isRpmAsset) => {
    if (!isRpmAsset) showAssetModal(title, asset);
  };
  const toggleClass = `accordionArrow${openSection === title ? 'Minus' : 'Plus'}`;
  const sectionSettings = fieldSettings?.find(({ section }) => section === sectionKey)

  const getSubtitle = (attributes, schema) => {
    if (schema.advSubtitle) {
      return schema.advSubtitle.replace(/:([a-zA-Z_ ]+):/g, (_, key) => attributes[key] || '');
    }
    let subtitle = attributes[schema.subtitle || schema.fields[0].id];
    if (schema.subtitle && Array.isArray(schema.subtitle)) {
      subtitle = schema.subtitle.map(_ => attributes[_]).join(' | ');
    }
    return subtitle;
  }

  const renderRpmLink = ({ rpmSection, page, guid, propertySlug, companyDisplayName }) => {
    const sections = { dump: 'dumpster', elec: 'electric' };
    const sectionPage = (spacePages && spacePages.filter(_ => _.section === rpmSection).sort((a, b) => a.index - b.index).findIndex(_ => _.index === page) + 1) || 1;
    return <>(<a rel="noopener noreferrer" target="_blank" href={`${process.env.REACT_APP_RPM_HOST}/${slugify(companyDisplayName)}/${propertySlug}/${sections[rpmSection] || rpmSection}?asset=${guid}&pg=${sectionPage}`}>RPM</a>)</>;
  }

  const assetCard = asset => {
    const { type, attributes, isRpmAsset } = asset;
    const schema = assetSchema[sectionKey].find(schema => schema.type === type);
    const subtitle = getSubtitle(attributes, schema);
    let cardTitle = asset.unit ? `${schema.displayName || type} • ${schema.unitText || t('ui.reference')} ${asset.unit}` : schema.displayName || type;
    if (isRpmAsset) cardTitle = <div>{cardTitle} {renderRpmLink(asset)}</div>;
    const iconType = schema.icon || (type ? type.toLowerCase() : null);
    return (
      <AssetCard
        className={schema.rpmType && asset.isRpmAsset ? styles.assetCardDisabled : styles.assetCard}
        title={cardTitle}
        subtitle={subtitle}
        iconType={iconType}
        onClick={() => handleCardClick(asset, schema.rpmType && asset.isRpmAsset)} />
    );
  };

  const doAssetCardRender = () => {
    return filteredAndSortedAssets ? filteredAndSortedAssets
      .map((asset, index) => {
        const visibilitySettings = sectionSettings?.assets?.find(({ type }) => type === asset.type)
        const visibilityClassName = visibilitySettings ? !visibilitySettings.visible ? 'd-none' : '' : ''
        return (
          <div key={`${asset.type}-${index}`} className={`col-sm-6 ${visibilityClassName}`}>
            {assetCard(asset, index)}
          </div>
        )
      }) : (<div className="col-sm-6">{t('AssetSection.noAssets')}</div>)
  }

  const sectionInfo = sectionSchema[sectionKey] || {};
  const assetCount = assets ? assets.length : 0;
  const showAddAssetButton =
    assetSchema[sectionKey].filter(_ => !_.rpmType).length // if non-rpm assets exist to be added for this section show the button
    && (sectionInfo.maxAssetCount === undefined || (assetCount < sectionInfo.maxAssetCount)); // if a maximum asset count is set, hide the button if we've reached the limit

  return (
    <Card>
      <Accordion.Toggle as={Card.Header} eventKey={title}>
        <h2 className='is-main-heading'>{ASSET_SECTION_LABELS[title]} <span className={classNames(styles.accordionArrow, styles[toggleClass])} /></h2>
      </Accordion.Toggle>
      <Accordion.Collapse eventKey={title}>
        <Card.Body className='py-4 px-0'>
          <div className="is-mechanical-accordian">
            <div className="row">
              {doAssetCardRender()}
            </div>
            {showAddAssetButton ?
              <div className='mt-3'>
                <Button className='btn-brand' variant="primary" size="lg" disabled={readOnly} onClick={() => showAssetModal(title)}>{intl.formatMessage({ id: 'AssetSection.addAsset' }, { asset: sectionInfo.addText || t('term.asset') })}</Button>
              </div>
              : null}
          </div>
        </Card.Body>
      </Accordion.Collapse>
    </Card>
  )
}
